import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Plus,
  Trash2,
  Shield,
  UserCog,
  Loader2,
  PowerOff,
  Power,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";
import { timeAgo, formatAbsolute } from "@/lib/time";
import { useAuth } from "@/context/AuthContext";
import { useAgency } from "@/context/AgencyContext";
import EditDistributorAccessDialog from "@/components/EditDistributorAccessDialog";

export default function UsersPage() {
  const { user: me } = useAuth();
  const { agencies } = useAgency();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "editor", agency_id: "" });
  const [saving, setSaving] = useState(false);
  const [accessUser, setAccessUser] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    if ((form.role === "agency_user" || form.role === "distributor") && !form.agency_id) {
      toast.error("Pick which agency this user belongs to");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        email: form.email,
        name: form.name,
        password: form.password,
        role: form.role,
        ...((form.role === "agency_user" || form.role === "distributor")
          ? { agency_id: form.agency_id }
          : {}),
      };
      await api.post("/users", payload);
      toast.success("User created");
      setOpen(false);
      setForm({ email: "", name: "", password: "", role: "editor", agency_id: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const setRole = async (u, role) => {
    try {
      await api.patch(`/users/${u.id}`, { role });
      toast.success(`Role updated to ${role}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setActive = async (u, active) => {
    try {
      await api.patch(`/users/${u.id}`, { active });
      toast.success(active ? "User reactivated" : "User deactivated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/users/${confirmDelete.id}`);
      toast.success("User deleted");
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data">
            Access control
          </div>
          <h1 className="mt-1 text-3xl font-display font-bold tracking-tight text-slate-900">
            Users
          </h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-xl">
            Invite team members to help build the database. Admins can manage
            users; editors can add and edit data.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            onClick={() => setOpen(true)}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
            data-testid="add-user-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add user
          </Button>
        </div>
      </div>

      <div className="bg-white border border-border rounded-sm overflow-hidden">
        {loading ? (
          <div className="byrd-progress" />
        ) : (
          <div className="overflow-x-auto" data-testid="users-table-scroll">
            <table className="byrd-table min-w-[820px]">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last login</th>
                <th className="w-32 sticky right-0 bg-slate-50 shadow-[-8px_0_8px_-4px_rgba(0,0,0,0.05)] z-10" />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="font-medium">
                    {u.name}
                    {u.id === me?.id && (
                      <span className="ml-2 text-[10px] uppercase font-mono-data text-amber-600">you</span>
                    )}
                    {u.role === "agency_user" && u.agency_name && (
                      <span className="ml-2 text-[10px] uppercase font-mono-data text-slate-500" data-testid={`user-agency-${u.id}`}>
                        · {u.agency_name}
                      </span>
                    )}
                  </td>
                  <td className="font-mono-data text-slate-600">{u.email}</td>
                  <td>
                    {u.id === me?.id || u.role === "agency_user" || u.role === "distributor" ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium">
                        {u.role === "admin" ? (
                          <Shield className="w-3.5 h-3.5 text-amber-600" />
                        ) : (
                          <UserCog className="w-3.5 h-3.5" />
                        )}
                        {u.role === "agency_user"
                          ? "Agency user"
                          : u.role === "distributor"
                          ? "Distributor"
                          : u.role}
                      </span>
                    ) : (
                      <Select value={u.role} onValueChange={(v) => setRole(u, v)}>
                        <SelectTrigger className="h-8 rounded-sm w-32" data-testid={`role-select-${u.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="editor">Editor</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  <td>
                    <span
                      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-sm border ${
                        u.active
                          ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                          : "bg-slate-100 border-border text-muted-foreground"
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${u.active ? "bg-emerald-500" : "bg-slate-400"}`} />
                      {u.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td
                    className="text-xs text-muted-foreground font-mono-data whitespace-nowrap"
                    title={formatAbsolute(u.last_login_at)}
                    data-testid={`last-login-${u.id}`}
                  >
                    {u.last_login_at ? timeAgo(u.last_login_at) : "—"}
                  </td>
                  <td className="sticky right-0 bg-white shadow-[-8px_0_8px_-4px_rgba(0,0,0,0.05)] z-10">
                    <div className="flex items-center gap-1">
                      {u.role === "distributor" && (
                        <button
                          onClick={() => setAccessUser(u)}
                          className="p-1.5 text-muted-foreground hover:text-amber-600"
                          data-testid={`edit-access-${u.id}`}
                          aria-label="Manage manufacturer access"
                          title="Manage manufacturer access"
                        >
                          <ShieldCheck className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {u.id !== me?.id && (
                        <>
                          <button
                            onClick={() => setActive(u, !u.active)}
                            className="p-1.5 text-muted-foreground hover:text-slate-900"
                            data-testid={`toggle-active-${u.id}`}
                            aria-label="Toggle active"
                            title={u.active ? "Deactivate" : "Reactivate"}
                          >
                            {u.active ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                          </button>
                          <button
                            onClick={() => setConfirmDelete(u)}
                            className="p-1.5 text-muted-foreground hover:text-destructive"
                            data-testid={`delete-user-${u.id}`}
                            aria-label="Delete user"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display">Add a user</DialogTitle>
            <DialogDescription>
              Create credentials for a team member. They can sign in immediately.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onCreate} className="space-y-4" data-testid="add-user-form">
            <div>
              <Label className="text-xs uppercase tracking-wider">Name *</Label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1.5 rounded-sm"
                data-testid="new-user-name"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Email *</Label>
              <Input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 rounded-sm"
                data-testid="new-user-email"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Temporary password *</Label>
              <Input
                type="text"
                required
                minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 rounded-sm font-mono-data"
                placeholder="Minimum 6 characters"
                data-testid="new-user-password"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Role *</Label>
              <Select
                value={form.role}
                onValueChange={(v) =>
                  setForm({
                    ...form,
                    role: v,
                    agency_id:
                      v === "agency_user" || v === "distributor" ? form.agency_id : "",
                  })
                }
              >
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="editor">Editor — can edit data</SelectItem>
                  <SelectItem value="admin">Admin — full access incl. users</SelectItem>
                  <SelectItem value="agency_user">Agency user — approvals only</SelectItem>
                  <SelectItem value="distributor">Distributor — search & quote requests</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(form.role === "agency_user" || form.role === "distributor") && (
              <div>
                <Label className="text-xs uppercase tracking-wider">Agency *</Label>
                <Select value={form.agency_id} onValueChange={(v) => setForm({ ...form, agency_id: v })}>
                  <SelectTrigger className="mt-1.5 rounded-sm" data-testid="new-user-agency">
                    <SelectValue placeholder="Pick an agency…" />
                  </SelectTrigger>
                  <SelectContent>
                    {agencies.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {form.role === "distributor"
                    ? "This distributor will be linked to this agency for search & quote requests."
                    : "This user will only see this agency's approvals page."}
                </p>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-slate-900 hover:bg-slate-800 rounded-sm"
                data-testid="new-user-save"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create user
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove "{confirmDelete?.name}". They will lose access immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-destructive hover:bg-red-600 text-white rounded-sm"
              data-testid="confirm-delete-user"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <EditDistributorAccessDialog
        open={!!accessUser}
        onOpenChange={(v) => !v && setAccessUser(null)}
        distributorUser={accessUser}
        onSaved={load}
      />
    </div>
  );
}
